import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MessageSquare,
  Search,
  MoreVertical,
  ChevronDown,
  PenSquare,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { messagingAPI } from '../services/messaging';
import wsClient from '../services/websocket';
import ConversationCard from '../components/ConversationCard';
import MessageBubble from '../components/MessageBubble';
import MessageInput from '../components/MessageInput';
import NewConversationModal from '../components/NewConversationModal';
import ManageConversationsModal from '../components/ManageConversationsModal';
import AwayMessageModal from '../components/AwayMessageModal';

function getCurrentUserId() {
  try {
    const raw = localStorage.getItem('user');
    const u = raw ? JSON.parse(raw) : null;
    return u?.user_id || u?.userId || u?.id || u?._id || null;
  } catch {
    return null;
  }
}

const MessagingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openConvId = searchParams.get('conversation') || null;

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesNextCursor, setMessagesNextCursor] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [activeFilter, setActiveFilter] = useState('focused');
  const [showFocusedDropdown, setShowFocusedDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [conversationMenuOpen, setConversationMenuOpen] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [manageConversationsOpen, setManageConversationsOpen] = useState(false);
  const [awayMessageOpen, setAwayMessageOpen] = useState(false);
  const [reportBlockOpen, setReportBlockOpen] = useState(false);
  const [reportAction, setReportAction] = useState({ report: true, block: true });
  const [reportTargetConversation, setReportTargetConversation] = useState(null);
  const [listMenuOpenId, setListMenuOpenId] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const currentUserId = getCurrentUserId();
  const currentUserRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      const u = raw ? JSON.parse(raw) : null;
      currentUserRef.current = u;
    } catch {
      currentUserRef.current = null;
    }
  }, [isAuthenticated]);

  const getSenderMeta = useCallback(() => {
    const user = currentUserRef.current || {};
    const senderName =
      user?.name ||
      user?.full_name ||
      user?.display_name ||
      user?.displayName ||
      [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
      user?.email ||
      'You';
    const senderPhoto =
      user?.photo_url ||
      user?.photoUrl ||
      user?.photo ||
      user?.profile_photo ||
      user?.profilePictureKey ||
      '';
    return { senderName, senderPhoto };
  }, []);

  const currentUserMeta = getSenderMeta();

  const archived = activeFilter === 'archived';
  const sortMessages = useCallback((list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
      const aTime = a?.created_at ? Date.parse(a.created_at) : 0;
      const bTime = b?.created_at ? Date.parse(b.created_at) : 0;
      return aTime - bTime;
    });
  }, []);

  const loadConversations = useCallback(async (override) => {
    const useArchived = override?.archived ?? archived;
    const useFilter = override?.filter ?? activeFilter;
    setLoading(true);
    try {
      const params = { archived: useArchived, limit: 100 };
      // Add filter parameter for server-side filtering
      if (useFilter && useFilter !== 'focused' && useFilter !== 'archived') {
        params.filter = useFilter;
      }
      const list = await messagingAPI.getConversations(params);
      setConversations(list);
    } catch (e) {
      console.error('Error loading conversations:', e);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [archived, activeFilter]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadConversations();
  }, [isAuthenticated, navigate, loadConversations]);

  useEffect(() => {
    if (!wsClient.isConfigured()) return;
    wsClient.connect();
    const handler = (data) => {
      if (data.action === 'typing' && data.conversation_id === selectedConversation?.conversation_id) {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        setTypingUser(data.sender_id);
        typingTimeoutRef.current = setTimeout(() => {
          setTypingUser(null);
          typingTimeoutRef.current = null;
        }, 3000);
      }
      if (data.action === 'new_message' && data.conversation_id === selectedConversation?.conversation_id) {
        const meta = getSenderMeta();
        const messageWithMeta = {
          ...data.message,
          sender_name: data.message?.sender_name || meta.senderName,
          sender_photo: data.message?.sender_photo || meta.senderPhoto,
        };
        setMessages((prev) => sortMessages([...prev, messageWithMeta]));
      }
      if (data.conversation_id && (data.action === 'new_message' || data.action === 'read_receipt')) {
        loadConversations();
        if (data.action === 'read_receipt' && data.conversation_id === selectedConversation?.conversation_id) {
          messagingAPI.getMessages(data.conversation_id, { limit: 50 }).then(({ messages: msgs }) => {
            setMessages(sortMessages(msgs));
          }).catch(() => {});
        }
      }
    };
    const unsubscribe = wsClient.onMessage(handler);
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      unsubscribe?.();
    };
  }, [selectedConversation?.conversation_id, loadConversations, sortMessages, getSenderMeta]);

  const selectConversation = useCallback(async (conv) => {
    setSelectedConversation(conv);
    setSearchParams({ conversation: conv?.conversation_id || '' }, { replace: true });
    setMessages([]);
    setMessagesNextCursor(null);
    if (!conv) return;
    setMessagesLoading(true);
    try {
      await messagingAPI.markConversationRead(conv.conversation_id);
      const { messages: msgs, next_cursor } = await messagingAPI.getMessages(conv.conversation_id, { limit: 50 });
      setMessages(sortMessages(msgs));
      setMessagesNextCursor(next_cursor);
      loadConversations();
      try {
        const raw = sessionStorage.getItem('messaging:draft');
        const draft = raw ? JSON.parse(raw) : null;
        if (draft && (draft.conversation_id === conv.conversation_id || (draft.to && (conv.participant_ids || []).includes(draft.to)))) {
          setMessageText(draft.text || '');
          sessionStorage.removeItem('messaging:draft');
        }
      } catch { /* ignore */ }
    } catch (e) {
      console.error('Error loading messages:', e);
    } finally {
      setMessagesLoading(false);
    }
  }, [loadConversations, setSearchParams]);

  useEffect(() => {
    if (!openConvId) return;
    if (selectedConversation?.conversation_id === openConvId) return;
    
    // First try to find in loaded conversations
    const c = conversations.find((x) => x.conversation_id === openConvId);
    if (c) {
      selectConversation(c);
      return;
    }
    
    // If not found in list, fetch it directly (handles new conversations)
    if (conversations.length > 0) {
      messagingAPI.getConversation(openConvId)
        .then((conv) => {
          if (conv) selectConversation(conv);
        })
        .catch((err) => {
          console.error('Failed to load conversation:', err);
        });
    }
  }, [openConvId, conversations, selectedConversation?.conversation_id, selectConversation]);

  const handleSendMessage = useCallback(async () => {
    const text = messageText.trim();
    if ((!text && !pendingAttachments.length) || !selectedConversation) return;
    const cid = selectedConversation.conversation_id;
    try {
      const msg = await messagingAPI.sendMessage(cid, { text: text || undefined, attachments: pendingAttachments });
      const meta = getSenderMeta();
      setMessages((prev) => sortMessages([
        ...prev,
        {
          ...msg,
          sender_id: currentUserId,
          sender_name: msg?.sender_name || meta.senderName,
          sender_photo: msg?.sender_photo || meta.senderPhoto,
        }
      ]));
      setMessageText('');
      setPendingAttachments([]);
      loadConversations();
      if (wsClient.isConnected()) wsClient.sendNewMessageNotification(cid, msg);
    } catch (e) {
      console.error('Error sending message:', e);
    }
  }, [messageText, pendingAttachments, selectedConversation, currentUserId, loadConversations, getSenderMeta]);

  const handleAttach = useCallback(async (files) => {
    if (!files?.length) return;
    setUploadingAttachments(true);
    try {
      const uploaded = [];
      for (let i = 0; i < files.length; i++) {
        const a = await messagingAPI.uploadAttachment(files[i]);
        uploaded.push(a);
      }
      setPendingAttachments((prev) => [...prev, ...uploaded]);
    } catch (e) {
      console.error('Error uploading attachment:', e);
    } finally {
      setUploadingAttachments(false);
    }
  }, []);

  const removePendingAttachment = useCallback((index) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleTyping = useCallback(() => {
    if (selectedConversation && wsClient.isConnected()) {
      wsClient.sendTyping(selectedConversation.conversation_id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      try {
        await messagingAPI.deleteMessage(messageId);
        setMessages((prev) => prev.filter((m) => m.message_id !== messageId));
      } catch (e) {
        console.error('Error deleting message:', e);
      }
    },
    []
  );

  const handleArchiveConversation = useCallback(
    async (archive, conv) => {
      const target = conv || selectedConversation;
      const cid = target?.conversation_id;
      if (!cid) return;
      setConversationMenuOpen(false);
      setListMenuOpenId(null);
      try {
        await messagingAPI.archiveConversation(cid, archive);
        if (!archive) setActiveFilter('focused');
        await loadConversations(!archive ? { archived: false } : undefined);
        if (selectedConversation?.conversation_id === cid) {
          setSelectedConversation(null);
          setSearchParams({}, { replace: true });
          setMessages([]);
        }
      } catch (e) {
        console.error('Error archiving conversation:', e);
      }
    },
    [selectedConversation?.conversation_id, selectedConversation, loadConversations, setSearchParams]
  );

  const handleDeleteConversation = useCallback(async (conv) => {
    const target = conv || selectedConversation;
    const cid = target?.conversation_id;
    if (!cid) return;
    if (!window.confirm('Delete this conversation? This cannot be undone for you.')) return;
    setConversationMenuOpen(false);
    setListMenuOpenId(null);
    try {
      await messagingAPI.deleteConversation(cid);
      await loadConversations();
      if (selectedConversation?.conversation_id === cid) {
        setSelectedConversation(null);
        setSearchParams({}, { replace: true });
        setMessages([]);
      }
    } catch (e) {
      console.error('Error leaving conversation:', e);
    }
  }, [selectedConversation?.conversation_id, selectedConversation, loadConversations, setSearchParams]);

  const handleLabelJobs = useCallback(async (conv) => {
    const target = conv || selectedConversation;
    const cid = target?.conversation_id;
    if (!cid) return;
    setConversationMenuOpen(false);
    setListMenuOpenId(null);
    try {
      await messagingAPI.labelConversationJobs(cid);
      await loadConversations();
      if (selectedConversation?.conversation_id === cid) {
        setSelectedConversation((prev) => (prev ? { ...prev, message_type: 'job' } : prev));
      }
    } catch (e) {
      console.error('Error labeling conversation as jobs:', e);
    }
  }, [selectedConversation?.conversation_id, selectedConversation, loadConversations]);

  const handleMarkUnread = useCallback(async (conv) => {
    const target = conv || selectedConversation;
    const cid = target?.conversation_id;
    if (!cid) return;
    setConversationMenuOpen(false);
    setListMenuOpenId(null);
    try {
      await messagingAPI.markConversationUnread(cid);
      await loadConversations();
      if (selectedConversation?.conversation_id === cid) {
        setSelectedConversation((prev) => (prev ? { ...prev, unread_count: 1 } : prev));
      }
    } catch (e) {
      console.error('Error marking conversation unread:', e);
    }
  }, [selectedConversation?.conversation_id, selectedConversation, loadConversations]);

  const handleToggleStar = useCallback(async (conv) => {
    const target = conv || selectedConversation;
    const cid = target?.conversation_id;
    if (!cid) return;
    const next = !target?.starred;
    setConversationMenuOpen(false);
    setListMenuOpenId(null);
    try {
      await messagingAPI.starConversation(cid, next);
      await loadConversations();
      if (selectedConversation?.conversation_id === cid) {
        setSelectedConversation((prev) => (prev ? { ...prev, starred: next } : prev));
      }
    } catch (e) {
      console.error('Error starring conversation:', e);
    }
  }, [selectedConversation?.conversation_id, selectedConversation, loadConversations]);

  const handleToggleMute = useCallback(async (conv) => {
    const target = conv || selectedConversation;
    const cid = target?.conversation_id;
    if (!cid) return;
    const next = !target?.muted;
    setConversationMenuOpen(false);
    setListMenuOpenId(null);
    try {
      await messagingAPI.muteConversation(cid, next);
      await loadConversations();
      if (selectedConversation?.conversation_id === cid) {
        setSelectedConversation((prev) => (prev ? { ...prev, muted: next } : prev));
      }
    } catch (e) {
      console.error('Error muting conversation:', e);
    }
  }, [selectedConversation?.conversation_id, selectedConversation, loadConversations]);

  const handleReportBlock = useCallback(async () => {
    const target = reportTargetConversation || selectedConversation;
    const cid = target?.conversation_id;
    if (!cid) return;
    const participants = target?.participant_ids || [];
    const otherId = participants.find((id) => id && id !== currentUserId) || null;
    setConversationMenuOpen(false);
    setListMenuOpenId(null);
    setReportBlockOpen(false);
    try {
      await messagingAPI.reportConversation(cid, {
        report: reportAction.report,
        block: reportAction.block,
        target_user_id: otherId,
      });
      await loadConversations();
      setReportTargetConversation(null);
    } catch (e) {
      console.error('Error reporting/blocking:', e);
    }
  }, [reportTargetConversation, selectedConversation, currentUserId, reportAction, loadConversations]);

  const filteredConversations = searchQuery.trim()
    ? conversations.filter(
        (c) =>
          (c.display_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (c.last_message_preview || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  const focusedFilters = ['Focused', 'Archived'];
  const tabs = ['connections', 'jobs', 'unread', 'inmail'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 top-[73px] flex flex-col overflow-hidden bg-gray-50">
      {/* Top bar: fixed height, no growth */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-900 truncate">Messaging</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Search messages"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-48"
              />
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <button
              type="button"
              onClick={() => setNewConversationOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="New message"
            >
              <PenSquare className="w-5 h-5 text-gray-600" />
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setHeaderMenuOpen((o) => !o)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Messaging options"
              >
                <MoreVertical className="w-5 h-5 text-gray-600" />
              </button>
              {headerMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden="true"
                    onClick={() => setHeaderMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[200px]">
                    <button
                      type="button"
                      onClick={() => {
                        setHeaderMenuOpen(false);
                        setManageConversationsOpen(true);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Manage conversations
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setHeaderMenuOpen(false);
                        setAwayMessageOpen(true);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Set away message
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Tabs Row */}
        <div className="px-4 flex items-center gap-2">
          {/* Focused Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowFocusedDropdown(!showFocusedDropdown)}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                activeFilter === 'focused' || activeFilter === 'archived'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {activeFilter === 'focused' ? 'Focused' : activeFilter === 'archived' ? 'Archived' : 'Focused'}
              <ChevronDown className="w-4 h-4" />
            </button>
            {showFocusedDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden="true"
                  onClick={() => setShowFocusedDropdown(false)}
                />
                <div className="absolute top-full left-0 mt-0 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[120px]">
                  {focusedFilters.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => {
                        setActiveFilter(f.toLowerCase());
                        setShowFocusedDropdown(false);
                        loadConversations({ archived: f === 'Archived', filter: f.toLowerCase() });
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                        activeFilter === f.toLowerCase() ? 'bg-green-50 text-green-800 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Tab Buttons */}
          <button
            type="button"
            onClick={() => {
              setActiveFilter('connections');
              loadConversations({ archived: false, filter: 'connections' });
            }}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeFilter === 'connections'
                ? 'bg-green-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Connections
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveFilter('jobs');
              loadConversations({ archived: false, filter: 'jobs' });
            }}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeFilter === 'jobs'
                ? 'bg-green-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Jobs
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveFilter('unread');
              loadConversations({ archived: false, filter: 'unread' });
            }}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeFilter === 'unread'
                ? 'bg-green-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Unread
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveFilter('inmail');
              loadConversations({ archived: false, filter: 'inmail' });
            }}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeFilter === 'inmail'
                ? 'bg-green-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            InMail
          </button>
        </div>
      </div>

      {/* Main content: takes remaining height, no page scroll */}
      <div className="flex-1 min-h-0 flex bg-white overflow-hidden">
        <div className="w-1/3 border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  {archived ? 'No archived conversations.' : 'No conversations yet.'}
                </div>
              ) : (
                filteredConversations.map((c) => (
                  <ConversationCard
                    key={c.conversation_id}
                    conversation={c}
                    isSelected={selectedConversation?.conversation_id === c.conversation_id}
                    onClick={selectConversation}
                    menuOpen={listMenuOpenId === c.conversation_id}
                    onMenuToggle={() =>
                      setListMenuOpenId((prev) => (prev === c.conversation_id ? null : c.conversation_id))
                    }
                    onMenuClose={() => setListMenuOpenId(null)}
                    menuItems={[
                      { label: 'Label as Jobs', onClick: () => handleLabelJobs(c) },
                      { label: 'Mark as Unread', onClick: () => handleMarkUnread(c) },
                      { label: 'Star', onClick: () => handleToggleStar(c) },
                      { label: 'Mute', onClick: () => handleToggleMute(c) },
                      { label: 'Archive', onClick: () => handleArchiveConversation(true, c) },
                      {
                        label: 'Report / Block',
                        onClick: () => {
                          setReportAction({ report: true, block: true });
                          setReportTargetConversation(c);
                          setReportBlockOpen(true);
                          setListMenuOpenId(null);
                        },
                      },
                      { label: 'Delete conversation', onClick: () => handleDeleteConversation(c), danger: true },
                    ]}
                  />
                ))
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedConversation ? (
              <>
                <div className="flex-shrink-0 p-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {selectedConversation.photo_url ? (
                      <img
                        src={selectedConversation.photo_url}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-gray-600 font-semibold text-sm">
                          {(selectedConversation.display_name || '?')
                            .trim()
                            .split(/\s+/)
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{selectedConversation.display_name || 'Unknown'}</h3>
                      {selectedConversation.is_group && (
                        <p className="text-sm text-gray-600">
                          {(selectedConversation.participant_ids?.length || 0)} people
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 relative">
                    <button
                      type="button"
                      onClick={() => setConversationMenuOpen((o) => !o)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      aria-label="Conversation options"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </button>
                    {conversationMenuOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          aria-hidden="true"
                          onClick={() => setConversationMenuOpen(false)}
                        />
                        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[180px]">
                          <button
                            type="button"
                            onClick={handleLabelJobs}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Label as Jobs
                          </button>
                          <button
                            type="button"
                            onClick={handleMarkUnread}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Mark as Unread
                          </button>
                          <button
                            type="button"
                            onClick={handleToggleStar}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Star
                          </button>
                          <button
                            type="button"
                            onClick={handleToggleMute}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Mute
                          </button>
                          <button
                            type="button"
                            onClick={() => handleArchiveConversation(true)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Archive
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setReportAction({ report: true, block: true });
                              setReportTargetConversation(selectedConversation);
                              setReportBlockOpen(true);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Report / Block
                          </button>
                          <button
                            type="button"
                            onClick={handleDeleteConversation}
                            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            Delete conversation
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messagesLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                    </div>
                  ) : (
                    <>
                      {messages.map((m) => (
                        <MessageBubble
                          key={m.message_id}
                          message={m}
                          isOwn={m.sender_id === currentUserId}
                          onDelete={handleDeleteMessage}
                          conversationId={selectedConversation?.conversation_id}
                          currentUserName={currentUserMeta.senderName}
                          currentUserPhoto={currentUserMeta.senderPhoto}
                        />
                      ))}
                      {typingUser && typingUser !== currentUserId && (
                        <p className="text-sm text-gray-500 italic">Someone is typing...</p>
                      )}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {pendingAttachments.length > 0 && (
                  <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 flex flex-wrap gap-2 items-center">
                    {pendingAttachments.map((a, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm"
                      >
                        {a.name}
                        <button
                          type="button"
                          onClick={() => removePendingAttachment(i)}
                          className="text-gray-500 hover:text-red-600"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <MessageInput
                  value={messageText}
                  onChange={setMessageText}
                  onSend={handleSendMessage}
                  onTyping={handleTyping}
                  onAttach={handleAttach}
                  hasAttachments={pendingAttachments.length > 0}
                  disabled={uploadingAttachments}
                  placeholder="Write a message..."
                />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {newConversationOpen && (
          <NewConversationModal
            onClose={() => setNewConversationOpen(false)}
            onCreated={(conv) => {
              setNewConversationOpen(false);
              if (conv?.conversation_id) {
                setSearchParams({ conversation: conv.conversation_id }, { replace: true });
                selectConversation(conv);
              } else {
                loadConversations();
              }
            }}
          />
        )}

        {manageConversationsOpen && (
          <ManageConversationsModal
            onClose={() => setManageConversationsOpen(false)}
            onUpdated={() => loadConversations()}
          />
        )}

        {awayMessageOpen && (
          <AwayMessageModal onClose={() => setAwayMessageOpen(false)} />
        )}

        {reportBlockOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Report / Block</h2>
              </div>
              <div className="p-4 space-y-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={reportAction.report}
                    onChange={(e) => setReportAction((prev) => ({ ...prev, report: e.target.checked }))}
                  />
                  Report the conversation
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={reportAction.block}
                    onChange={(e) => setReportAction((prev) => ({ ...prev, block: e.target.checked }))}
                  />
                  Block the sender
                </label>
                <p className="text-xs text-gray-500">
                  Blocking prevents future messages from that sender.
                </p>
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setReportBlockOpen(false);
                    setReportTargetConversation(null);
                  }}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReportBlock}
                  disabled={!reportAction.report && !reportAction.block}
                  className="btn btn-primary"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
};

export default MessagingPage;
